import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

const ShortCard = ({ video, playing, onPlay }) => (
  <div
    className="snap-start shrink-0 w-[220px] sm:w-[260px] group"
    data-testid={`short-card-${video.id}`}
  >
    <div className="relative aspect-[9/16] overflow-hidden border border-white/10 bg-black">
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${video.youtube_video_id}?autoplay=1&playsinline=1&rel=0`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
          data-testid={`short-iframe-${video.id}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => onPlay(video.id)}
          data-testid={`short-play-${video.id}`}
          className="absolute inset-0 w-full h-full text-left"
          aria-label={`Play ${video.title}`}
        >
          <img
            src={video.image_url}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-50"
          />
          <img
            src={video.image_url}
            alt={video.title}
            loading="lazy"
            className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-[#0D0D10] via-transparent to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#FF375F] text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play size={18} fill="currentColor" className="ml-0.5" />
            </span>
          </span>
          <span className="absolute bottom-0 left-0 right-0 p-3">
            <span className="block text-[13px] font-semibold leading-snug text-white line-clamp-3">{video.title}</span>
          </span>
        </button>
      )}
    </div>
    <Link
      to={`/article/${video.id}`}
      data-testid={`short-read-${video.id}`}
      className="mt-2 inline-flex items-center gap-1 font-mono-meta text-[10px] uppercase tracking-widest text-slate-400 hover:text-[#FFD400] transition-colors"
    >
      पूरी खबर पढ़ें <ArrowUpRight size={11} />
    </Link>
    {playing && (
      <a
        href={video.youtube_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`short-youtube-${video.id}`}
        className="mt-1 block font-mono-meta text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#FF375F] transition-colors"
      >
        नहीं चल रहा? YouTube पर देखें
      </a>
    )}
  </div>
);

export default function ShortsReel({ videos }) {
  const scroller = useRef(null);
  const [playing, setPlaying] = useState(null);
  if (!videos?.length) return null;

  const nudge = (dir) => scroller.current?.scrollBy({ left: dir * 560, behavior: "smooth" });

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 mt-16"
      data-testid="shorts-reel"
    >
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <span className="font-mono-meta text-xs text-[#FF375F] uppercase tracking-[0.3em]">खबरिक शॉर्ट्स</span>
          <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">देखिए, एक नज़र में</h2>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/category/video"
            data-testid="shorts-view-all"
            className="hidden sm:inline-flex font-mono-meta text-[10px] uppercase tracking-widest text-slate-400 hover:text-[#FFD400] transition-colors mr-2"
          >
            सभी वीडियो
          </Link>
          <button
            onClick={() => nudge(-1)}
            data-testid="shorts-prev"
            className="w-9 h-9 flex items-center justify-center border border-white/15 text-slate-300 hover:text-[#FFD400] hover:border-[#FFD400] transition-colors"
            aria-label="पिछला"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => nudge(1)}
            data-testid="shorts-next"
            className="w-9 h-9 flex items-center justify-center border border-white/15 text-slate-300 hover:text-[#FFD400] hover:border-[#FFD400] transition-colors"
            aria-label="अगला"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div
        ref={scroller}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 no-scrollbar"
        data-testid="shorts-scroller"
      >
        {videos.map((v) => (
          <ShortCard key={v.id} video={v} playing={playing === v.id} onPlay={setPlaying} />
        ))}
      </div>
    </motion.section>
  );
}
